<script setup>
import { useI18n } from 'vue-i18n';
import { ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import axios from 'axios';
import { compressImageFile, convertCanvasToFile } from '@utils';
import { Cropper, CircleStencil } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';

const toast = useToast();

const { t } = useI18n({ useScope: 'global' });
const filesUrl = 'https://proctoring-api.univora.uz/services/platon-core/uploads/files/';
const fileInput = ref(null);

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ACCESSED_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];

const loading = ref(false);
const cropperRef = ref(null);
const cropModal = ref(false);
const cropImage = ref(null);

const { name } = defineProps({
    name: String,
});
const uploadedAvatar = defineModel({
    default: null,
});

const uploadImageHandler = async (e) => {
    const [file] = e.target.files;
    if (file.size > MAX_FILE_SIZE) {
        toast.add({ severity: 'warn', summary: t('uploadAvatar.accessed-size-image-warning-message', { val: 5 }), life: 3000 });
        return;
    }
    if (!ACCESSED_TYPES.includes(file.type)) {
        toast.add({ severity: 'warn', summary: t('uploadAvatar.accessed-types-image-warning-message'), life: 3000 });
        return;
    }

    loading.value = true;
    cropModal.value = true;
    cropImage.value = URL.createObjectURL(file);
    loading.value = false;
};

const deleteImageHandler = () => {
    loading.value = true;
    if (uploadedAvatar.value) {
        uploadedAvatar.value = null;
        fileInput.value.value = null;
    }
    loading.value = false;
};

const onCrop = async () => {
    const croppedImage = cropperRef.value.getResult().canvas;
    cropModal.value = false;

    const file = await convertCanvasToFile(croppedImage, 'cropped-image.jpg');

    const photo = await uploadImage(file, 'image');
    uploadedAvatar.value = photo;
};

const flip = (x, y) => {
    const { image } = cropperRef.value.getResult();
    if (image.transforms.rotate % 180 !== 0) {
        cropperRef.value.flip(!x, !y);
    } else {
        cropperRef.value.flip(x, y);
    }
};

const rotate = (angle) => {
    cropperRef.value.rotate(angle);
};

const resize = (width = 1, height = 1) => {
    let startCoordinates;
    cropperRef.value.setCoordinates(({ coordinates, imageSize }) => ({
        width: imageSize.width,
        height: imageSize.height,
    }));
};

const uploadImage = async (file) => {
    const compressedImage = await compressImageFile(file);

    const formDataCompressed = new FormData();
    formDataCompressed.append('file', compressedImage);

    try {
        const response = await axios.post('https://proctoring-api.univora.uz/services/platon-core/web/v1/public/files/upload/category/profile', formDataCompressed, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data.id;
    } catch (e) {
        console.log(e);
    }
};
</script>

<template>
    <Dialog v-model:visible="cropModal" modal :header="t('uploadAvatar.avatar-title')" :style="{ width: '600px' }">
        <p class="upload-description">{{ t('uploadAvatar.avatar-full-description') }}</p>
        <div class="cropper-wrapper">
            <Cropper ref="cropperRef" class="cropper" :src="cropImage" :stencil-component="CircleStencil" />
            <div class="actions-ava">
                <Button @click="resize(2, 2)">
                    <Icon name="expand" class="upload-icon" />
                </Button>
                <Button @click="flip(true, false)">
                    <Icon name="flip" class="upload-icon" />
                </Button>
                <Button @click="flip(false, true)">
                    <Icon name="flip" class="upload-icon" style="transform: rotate(90deg)" />
                </Button>
                <Button @click="rotate(90)">
                    <Icon name="rotate-right" class="upload-icon" />
                </Button>
                <Button @click="rotate(-90)">
                    <Icon name="rotate-left" class="upload-icon" />
                </Button>
            </div>
        </div>

        <div class="flex justify-end gap-2 mt-4">
            <Button type="button" :label="t('uploadAvatar.cancel')" severity="secondary" @click="cropModal = false"></Button>
            <Button type="button" :label="t('uploadAvatar.save')" severity="info" @click="onCrop"></Button>
        </div>
    </Dialog>

    <div class="upload-wrapper">
        <Htag tag="h3" class="upload-title">{{ $t('uploadAvatar.avatar-title') }}</Htag>
        <div class="ava-drop-zone">
            <div class="ava-left-zone">
                <sAvatar v-if="!loading" size="large" :name="name" :src="uploadedAvatar && filesUrl + uploadedAvatar" />
                <div v-else class="loading-ava">
                    <ProgressSpinner style="width: 20px; height: 20px" strokeWidth="8" />
                </div>

                <div class="ava-text-info">
                    <span class="ava-text-title">{{ $t('uploadAvatar.avatar-description') }}</span>
                    <span class="ava-text-description"> {{ $t('uploadAvatar.avatar-file-types') }} </span>
                </div>
            </div>
            <div class="ava-right-zone">
                <Button icon="pi pi-trash" severity="danger" rounded variant="outlined" @click="deleteImageHandler" v-if="uploadedAvatar" />

                <label for="file" class="upload-btn-label">
                    <i class="pi pi-upload"></i>
                    <input @change="uploadImageHandler" ref="fileInput" type="file" id="file" class="hidden" accept=".png, .jpg, .jpeg" />
                </label>
            </div>
        </div>
    </div>
</template>
<style scoped>
.upload-description {
    @apply text-black text-xs font-normal text-center pb-4;
}

.cropper-wrapper {
    @apply flex relative;
}

.cropper {
    @apply max-h-[600px];
}

.vue-advanced-cropper {
    @apply overflow-hidden;
}

.upload-title {
    @apply text-black mb-2;
}

.upload-wrapper {
    @apply bg-gray-100 py-6 px-8;
}

.ava-drop-zone {
    @apply flex justify-between px-4 py-3 bg-white rounded-xl;
}

.ava-left-zone {
    @apply flex gap-3;
}

.ava-right-zone {
    @apply flex gap-2 items-center;
}

.ava-text-info {
    @apply flex flex-col justify-center gap-1;
}

.ava-text-title {
    @apply text-black text-sm font-medium;
}

.ava-text-description {
    @apply text-gray-600 text-xs font-normal;
}

.upload-btn-label {
    @apply flex items-center justify-center rounded-full p-3 cursor-pointer transition-all ease-linear duration-100 text-xl hover:bg-sky-50 border border-sky-200 text-sky-500;
}

.upload-icon {
    @apply w-6 h-6;
}

.loading-ava {
    @apply flex items-center justify-center bg-gray-200 w-12 h-12 rounded-full;
}

.actions-ava {
    @apply flex absolute items-center gap-2 mt-6 w-full justify-center bottom-4;
}
.actions-ava .p-button {
    @apply !p-2;
}
</style>
